import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Centralized access token hook.
 *
 * SECURITY: This is the ONLY place in the app that should extract
 * access tokens. All components must use this hook instead of
 * reading from localStorage directly.
 *
 * The hook:
 * 1. Gets the current session token from Supabase (server-validated)
 * 2. Listens for token refreshes and updates automatically
 * 3. Returns null if no valid session exists
 */
export default function useAccessToken() {
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Get initial token from current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setToken(session?.access_token || null);
    });

    // Listen for session changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setToken(session?.access_token || null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return token;
}

/**
 * Get access token synchronously (for non-hook contexts).
 * SECURITY: This reads from Supabase's managed storage, not raw localStorage.
 * Falls back gracefully if no session exists.
 */
export function getAccessTokenSync() {
  try {
    const url = process.env.REACT_APP_SUPABASE_URL || '';
    const ref = url ? new URL(url).hostname.split('.')[0] : '';
    const raw = localStorage.getItem(`sb-${ref}-auth-token`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Verify token isn't expired
    if (parsed?.expires_at && parsed.expires_at <= Date.now() / 1000) {
      return null; // Expired
    }
    return parsed?.access_token || null;
  } catch {
    return null;
  }
}
